import { WrenchIcon } from '@phosphor-icons/react'
import { type FixCounts, type Tool } from '../../utils/api-client/api-schemas'
import { TooltipWithHover } from '../tooltip'
import { type FixSegment, mapFixToLabels, mapFixToSegments } from './composition-utils'
import { SegmentsBar, type SegmentsBarSize } from './segments-bar'
import * as barStyles from './segments-bar.css'
import { NoAnalysisCircle, SegmentsLabels } from './segments-labels'
import * as labelStyles from './segments-labels.css'

type FixCompositionBarProps = {
  statistics: FixCounts
  tool: Tool
  size?: SegmentsBarSize
  isTransitioning?: boolean
  onSegmentClick?: (label: FixSegment) => unknown
  selectedSegments?: Iterable<FixSegment>
  nonClickableSegments?: FixSegment[]
  visibleSegments?: FixSegment[]
}

export const FixCompositionBar: React.FC<FixCompositionBarProps> = ({
  statistics,
  tool,
  size = 'tiny',
  isTransitioning,
  onSegmentClick,
  selectedSegments = new Set<FixSegment>(),
  nonClickableSegments = [],
  visibleSegments,
}) => {
  const selectedSegmentsSet = selectedSegments instanceof Set ? selectedSegments : new Set(selectedSegments)
  const allSegments = mapFixToSegments(statistics, tool)

  if (isTransitioning) {
    const skeletonSegments = allSegments.map(s => ({
      ariaLabel: s.ariaLabel,
      percentage: s.percentage,
      color: '',
      className: barStyles.skeletonSegment,
    }))
    return <SegmentsBar segments={skeletonSegments} size={size} />
  }

  const allLabels = mapFixToLabels(statistics, tool).map(label =>
    label.name === 'No fix' ? { ...label, icon: <NoAnalysisCircle /> } : label
  )
  const labels = visibleSegments ? allLabels.filter(l => visibleSegments.includes(l.name)) : allLabels

  const hasActiveFilters = selectedSegmentsSet.size > 0
  const isHiddenByQuickView = (ariaLabel: string) =>
    visibleSegments ? !visibleSegments.includes(ariaLabel as FixSegment) : false
  const segments = allSegments.map(segment => ({
    ...segment,
    pressed: selectedSegmentsSet.has(segment.ariaLabel),
    dimmed: isHiddenByQuickView(segment.ariaLabel) || (hasActiveFilters && !selectedSegmentsSet.has(segment.ariaLabel)),
    onClick:
      onSegmentClick && !nonClickableSegments.includes(segment.ariaLabel) && !isHiddenByQuickView(segment.ariaLabel)
        ? () => onSegmentClick(segment.ariaLabel)
        : undefined,
  }))

  return (
    <TooltipWithHover
      trigger={<SegmentsBar segments={segments} size={size} />}
      maxWidth={1200}
      side="bottom"
      sideOffset={8}
      bare
    >
      <SegmentsLabels labels={labels} icon={<WrenchIcon className={labelStyles.leadingIconSvg} weight="light" />} />
    </TooltipWithHover>
  )
}
